'use client';

import React, { useState } from 'react';

import Link from 'next/link';
import { usePathname } from 'next/navigation';

import { useStoreRedirect } from '@/hooks/useStoreRedirect';
import { CmsImage } from '@/components/CmsImage';
import { getRedirectTrackingAttrs } from '@/lib/pageClickUtils';
import { buildSubPageHref } from '@/lib/pageRedirectUtils';
import { PageRedirectConfig, PageResponse } from '@/lib/types';
import { cn, preserveQueryParams } from '@/lib/utils';
import { useAppState } from '@/state/hooks/app.state.hook';

import { MenuSubItem } from './MenuSubItem';
import { ChevronDown } from 'lucide-react';

export const DEFAULT_TEXT_COLOR = '#000';

interface IMenuItemProps {
  icon: React.ReactNode;
  title: string;
  to?: string;
  isDisabled?: boolean;
  subPages?: PageResponse[];
  onSubItemClick?: () => void;
  redirectConfig?: PageRedirectConfig;
  'data-testid'?: string;
}

export const getTabStateClass = (isActive: boolean, isHovered: boolean): string | undefined => {
  if (isActive) return 'menu-item-active';
  if (isHovered) return 'menu-item-hover';

  return undefined;
};

export const MenuItem: React.FC<IMenuItemProps> = ({
  icon,
  title,
  to,
  isDisabled = false,
  subPages = [],
  onSubItemClick,
  redirectConfig,
  'data-testid': dataTestId,
}) => {
  const [isHovered, setIsHovered] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const { setCurrentPageName } = useAppState();
  const { redirect } = useStoreRedirect();
  const pathname = usePathname();
  const isSubPage = subPages.length > 0;
  const isActive = pathname === to;
  const isRedirect = Boolean(redirectConfig?.enabled && redirectConfig.redirectUrl);

  const handleClick = (e: React.MouseEvent) => {
    if (isRedirect) {
      e.preventDefault();
      redirect(redirectConfig!.redirectUrl, redirectConfig!.isStoreRedirect);
      return;
    }

    setCurrentPageName(title);
  };

  const handleArrowClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsExpanded(!isExpanded);
  };

  const href = isDisabled || isRedirect ? '' : preserveQueryParams(to || '/');

  return (
    <>
      <Link
        href={href}
        onClick={handleClick}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        data-testid={dataTestId}
        {...getRedirectTrackingAttrs(isRedirect, redirectConfig)}
        className={cn(
          `menu-item flex w-full cursor-pointer items-center justify-between rounded-[20px] font-bold`,
          getTabStateClass(isActive, isHovered)
        )}
      >
        {/* The item is padding free so the divider can span its full height;
            the icon and label carry that spacing as margins instead. */}
        <div
          className={cn(
            'ml-[18px] flex min-w-0 flex-1 items-center gap-x-3',
            !isSubPage && 'mr-[18px]'
          )}
        >
          {icon}
          <div className="menu-divider hidden mt-[2px] mb-1 w-0.5 shrink-0 self-stretch [background:repeating-linear-gradient(180deg,#F3BDB2_0_4px,transparent_0_7px)_0_0/1px_7px_repeat-y,repeating-linear-gradient(180deg,#FFEDE4_0_4px,transparent_0_7px)_1px_0/1px_7px_repeat-y]" />
          <div className="menu-label my-2.5 min-w-0 truncate text-xl">{title}</div>
        </div>
        {isSubPage && (
          <ChevronDown
            onClick={handleArrowClick}
            className={cn(
              'mr-[18px] size-6 min-h-6 min-w-6 transition-transform duration-300 ease-in-out',
              isExpanded && '-rotate-180'
            )}
          />
        )}
      </Link>
      {isSubPage && isExpanded && (
        <div
          className={cn(
            'menu-sub-items mx-[18px] mt-3 flex flex-col gap-3.5 overflow-hidden transition-all duration-300 ease-in-out'
          )}
        >
          {subPages.map((subPage) => (
            <MenuSubItem
              key={subPage.id}
              data-testid={`PortalSidebar-SubItem-Link-${subPage.slug}`}
              to={buildSubPageHref(to, subPage.slug)}
              title={subPage.pageName}
              icon={
                subPage.pageIcon ? (
                  <CmsImage
                    src={subPage.pageIcon}
                    alt={subPage.pageTemplate}
                    width={32}
                    height={32}
                    className="menu-icon size-8 shrink-0"
                  />
                ) : null
              }
              onClick={onSubItemClick}
            />
          ))}
        </div>
      )}
    </>
  );
};

export default MenuItem;
